import { ReactElement, useCallback } from 'react';

import { FieldProps, useField, FieldRenderProps } from 'react-final-form';

import { ScaleBarInput as Input } from '@vk-hr-tek/ui/input';

/**
 * Свойства компонента {@link ScaleBarInput}
 */
interface ScaleBarInputProps {
  /**
   * Целевое значение шкалы.
   */
  target: number;

  /**
   * Флаг для отображения метки.
   */
  showLabel?: boolean;

  /**
   * Функция для рендера подсказки.
   */
  renderTooltip?: (index: number) => ReactElement | undefined;

  /**
   * Флаг для отключения компонента.
   */
  disabled?: boolean;

  /**
   * Флаг для отображения пустого значения, если поле не изменялось.
   */
  showEmptyIfPristine?: boolean;
}

/**
 * Компонент ScaleBarInput для рендеринга поля ввода с шкалой.
 *
 * @public
 * @param renderTooltip - Функция для рендера подсказки.
 * @param target - Целевое значение шкалы.
 * @param showLabel - Флаг для отображения метки.
 * @param disabled - Флаг для отключения компонента.
 * @param showEmptyIfPristine - Флаг для отображения пустого значения, если поле не изменялось.
 * @param name - Имя поля ввода.
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Элемент React с шкалой для ввода.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ScaleBarInputProps}
 */
export const ScaleBarInput = ({
  renderTooltip,
  target,
  showLabel,
  disabled,
  showEmptyIfPristine,
  name,
  ...rest
}: ScaleBarInputProps &
  FieldProps<number | undefined, FieldRenderProps<number | undefined>>) => {
  const { input, meta } = useField(name, rest);
  const handleChange = useCallback(
    (onChange: (newValue: number) => void) => (newValue: number) => {
      onChange(newValue);
      input.onBlur();
    },
    [input],
  );
  return (
    <Input
      {...input}
      target={target}
      showLabel={showLabel}
      renderTooltip={renderTooltip}
      disabled={disabled}
      value={meta.touched || !showEmptyIfPristine ? input.value : 0}
      onChange={handleChange(input.onChange)}
    />
  );
};
